import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges
} from '@angular/core';
import {
    FormArray,
    FormBuilder,
    FormControl,
    FormGroup,
    Validators
} from '@angular/forms';
import { formatDate } from '@angular/common'; // Import this for date formatting
import { FinanceMargin, FinanceMarginStatus } from '../../finance.types';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { truncTo2Dec } from '../../finance.helpers';

@Component({
    selector: 'finance-margins',
    templateUrl: './finance-margins.component.html',
    styleUrls: ['./finance-margins.component.scss']
})

export class FinanceMarginsComponent implements OnInit, OnChanges {
    @Output() marginsChanged = new EventEmitter();
    @Input() items: FinanceMargin[] = [];
    @Input() currencyCode: string = 'GBP';
    @Input() readonly: boolean = false;
    @Input() allowNegativeTotal: boolean = false;
    @Input() vatDisabled: boolean = false;
    @Input() financeSubTotal: number = 0;

    // Main form for repeated rows
    form: FormGroup;
    // Separate form for the initial input
    initialInput: FormGroup;
    totalMargin: number = 0;
    currencyChangeHack: boolean = true;

    // View flags
    showInitialInput: boolean = false;
    showRepeatedRows: boolean = false;
    marginsHaveChanged: boolean = false;

    defaultStatus: FinanceMarginStatus;
    defaultValue: number;
    defaultIncluded: boolean;

  statusChoices: { value: FinanceMarginStatus; label: string }[] = [
      { value: FinanceMarginStatus.None, label: 'None' },
      { value: FinanceMarginStatus.Complete, label: 'Complete' },
      { value: FinanceMarginStatus.Opportunity, label: 'Opportunity' },
      { value: FinanceMarginStatus.AtRisk, label: 'At Risk' },
      { value: FinanceMarginStatus.Lost, label: 'Lost' }
  ];

    constructor(private _fb: FormBuilder) {
        // The repeated rows
        this.form = this._fb.group({
            items: this._fb.array([])
        });

        // The initial input form
        this.initialInput = this._fb.group({
            numberOfMonths: [null, [Validators.required, Validators.min(1)]],
            status: [FinanceMarginStatus.Opportunity],
            value: [null, Validators.required],
            included: [true]
        });
    }

    ngOnInit() {
        // If items exist at initialization, show them
        if (this.items && this.items.length > 0) {
            this.items.forEach(item => this.addItem(item));
            this.showRepeatedRows = true;
        }
        this.calcTotals();

        // Listen to changes on the repeated rows
        this.form.valueChanges.subscribe(values => {
            this.handleOrdering(values.items);
            this.calcTotals();
            this.handleItemsChanges();
        });
    }

    ngOnChanges(changes: SimpleChanges) {
        if ('currencyCode' in changes) {
            // Hack to force the currency input formatter to reload
            this.currencyChangeHack = false;
            setTimeout(() => (this.currencyChangeHack = true), 0);
        }
    }

    getItems(): FormArray {
      return this.form.get('items') as FormArray;
    }

    onAddMarginValue() {
        this.initialInput.reset({
            numberOfMonths: 1,
            status: FinanceMarginStatus.Opportunity,
            value: null,
            included: true,
        });
        this.showInitialInput = true;
    }

    onEnter() {
        const numMonths = this.initialInput.get('numberOfMonths').value;
        const status = this.initialInput.get('status').value;
        const value = this.initialInput.get('value').value;
        const included = this.initialInput.get('included').value;

        // Save the defaults for later added rows
        this.defaultStatus = status;
        this.defaultValue = value;
        this.defaultIncluded = included;

        // Clear any existing items
        const itemsArray = this.getItems();
        while (itemsArray.length) {
            itemsArray.removeAt(0);
        }

        const now = new Date();
        for (let i = 0; i < numMonths; i++) {
            const monthDate = new Date(now.getFullYear(), now.getMonth() + i, 1);
            this.getItems().push(
                this._fb.group({
                    month: [monthDate],
                    status: [status],
                    value: [value],
                    included: [included]
                })
            );
        }

        this.showRepeatedRows = true;
        this.showInitialInput = false;
        this.calcTotals();
        this.marginsHaveChanged = true;
        this.handleItemsChanges();
    }

    onAdd() {
        const itemsArray = this.getItems();
        let newMonth: Date;
        if (itemsArray.length > 0) {
            // Calculate the new month based on the last row’s month value.
            let lastItem = itemsArray.at(itemsArray.length - 1);
            let lastMonthValue = lastItem.get('month').value;
            if (!(lastMonthValue instanceof Date)) {
                lastMonthValue = new Date(lastMonthValue);
            }
            newMonth = new Date(lastMonthValue.getFullYear(), lastMonthValue.getMonth() + 1, 1);
        } else {
            // Fallback: use the current month.
            const now = new Date();
            newMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        }

        // Determine default values:
        // Use the initialInput value for 'value' if provided; otherwise, default to empty.
        // For status, default to Opportunity if no value is entered.
        // For the Live checkbox, default to true.
        let defaultValue: any = '';
        let defaultStatus: FinanceMarginStatus = this.initialInput.get('status').value || FinanceMarginStatus.Opportunity;
        let defaultIncluded: boolean = (this.initialInput.get('included').value !== null &&
                                          this.initialInput.get('included').value !== undefined)
                                        ? this.initialInput.get('included').value
                                        : true;
        const initValue = this.initialInput.get('value').value;
        if (initValue !== null && initValue !== undefined && initValue !== '') {
            defaultValue = initValue;
        }

        this.addItem({
            month: newMonth,
            status: defaultStatus,
            value: defaultValue,
            included: defaultIncluded
        } as FinanceMargin);
    }

    onRemove(index: number) {
        this.getItems().removeAt(index);
        if (this.getItems().length === 0) {
            this.showRepeatedRows = false;
            this.initialInput.reset();
        }
    }

    drop(event: CdkDragDrop<string[]>) {
        moveItemInArray(
            this.getItems().controls,
            event.previousIndex,
            event.currentIndex
        );
        moveItemInArray(this.items, event.previousIndex, event.currentIndex);
        this.form.markAsDirty();
        this.handleOrdering(this.items);
        this.handleItemsChanges();
    }

    addItem(item: FinanceMargin) {
        let monthDate;
        if (item.month instanceof Date) {
            monthDate = item.month;
        } else if (typeof item.month === 'string') {
            // Convert a stored string (e.g. "2025-03") to a Date object.
            monthDate = item.month ? new Date(item.month) : null;
        }
        this.getItems().push(
            this._fb.group({
                month: new FormControl({ value: monthDate, disabled: false }),
                status: new FormControl({ value: item.status, disabled: false }),
                value: new FormControl({ value: item.value || null, disabled: false }),
                included: new FormControl({ value: item.included, disabled: false }),
            })
        );
    }

    getFormattedMonth(date: Date): string {
        return date ? formatDate(date, 'MMM yy', 'en-GB') : '';
    }

    onMonthSelected(event: any, item: FormGroup) {
        // Set the month value to the selected month (ensure event is a Date or proper value)
        item.patchValue({ month: event });
        this.form.markAsDirty();
        this.handleItemsChanges();
    }

    onMonthCleared(event: any, item: FormGroup) {
        // Clear the month value
        item.patchValue({ month: null });
        this.form.markAsDirty();
        this.handleItemsChanges();
    }

    handleOrdering(items: any[]) {
        this.items = items.map((item, index) => ({
            ...item,
            value: item.value ? truncTo2Dec(item.value) : null,
            order: index
        }));
        this.calcTotals();
    }

    handleItemsChanges() {
        this.marginsChanged.emit({
          items: this.items,
          total: this.totalMargin,
          dirty: this.form.dirty || this.marginsHaveChanged,
          form: this.form,
          valid:
              this.form.valid &&
              (this.allowNegativeTotal ||
                  (!this.allowNegativeTotal && this.totalMargin > 0))
        });
    }

    calcTotals() {
        const round = (num) => Math.round((num + Number.EPSILON) * 100) / 100;
        this.totalMargin =
            this.items?.reduce((sum, x) => {
                return round(sum + stripChars(x.value));
            }, 0) ?? 0;

        function stripChars(value: string | number | null): number {
            if (value == null || value === '') return 0;
            return parseFloat(value.toString().replace(/[^\d.-]/g, ''));
        }
    }

    isMarginGreaterThanTotal(): boolean {
        const sum = this.items.reduce((acc, x) => acc + (x.value || 0), 0);
        return sum > this.financeSubTotal;
    }
}
